import React, { useEffect, useMemo, useState } from 'react';
import { Handle, NodeProps, Position, useUpdateNodeInternals, useNodeId } from 'reactflow';
import type { SidebarDragPayload } from '../Sidebar/sidebarPalette';
import { SIDEBAR_DRAG_MIME, sidebarPayloadFromDragJson } from '../Sidebar/sidebarPalette';
import type { LlmNodeSettings, LlmOperation, McpStep } from '../llmWorkflowTypes';
import { MCP_SOFT_LIMIT, truncatePrompt } from '../llmWorkflowTypes';
import './LLMNode.css';

export type LlmNodeData = {
  label: string;
  paletteType: string;
} & LlmNodeSettings & {
    onPatch: (patch: Partial<LlmNodeSettings & { label?: string }>) => void;
    onReorderMcp: (fromIndex: number, toIndex: number) => void;
    onRemoveMcp: (stepId: string) => void;
    onDropMcpPalette: (payload: SidebarDragPayload) => void;
  };

const OPS: LlmOperation[] = ['Analyze', 'Summarize', 'Generate', 'Classify', 'Extract'];

function chipIcon(kind: McpStep['kind']): string {
  switch (kind) {
    case 'database':
      return '🗄';
    case 'knowledge_base':
      return '📚';
    case 'api':
      return '🔌';
    case 'event':
      return '⚡';
    default:
      return '◆';
  }
}

function chipSuffix(step: McpStep): string {
  if (step.kind === 'database' && step.database) {
    return step.database.operation;
  }
  if (step.kind === 'event') {
    return 'emit';
  }
  if (step.kind === 'knowledge_base') {
    return 'read';
  }
  if (step.kind === 'api') {
    return 'call';
  }
  return '';
}

export default function LLMNode({ data, selected }: NodeProps<LlmNodeData>) {
  const nodeId = useNodeId();
  const updateInternals = useUpdateNodeInternals();
  const [dragMcpIndex, setDragMcpIndex] = useState<number | null>(null);
  useEffect(() => {
    if (nodeId) updateInternals(nodeId);
  }, [nodeId, updateInternals, data.mcpSteps.length, data.parentPrompt]);

  const preview = useMemo(() => truncatePrompt(data.parentPrompt, 80), [data.parentPrompt]);

  const onHeaderOpChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    data.onPatch({ operation: e.target.value as LlmOperation });
  };

  const parsePaletteDrop = (event: React.DragEvent): SidebarDragPayload | null => {
    const raw = event.dataTransfer.getData(SIDEBAR_DRAG_MIME);
    if (!raw) return null;
    return sidebarPayloadFromDragJson(raw);
  };

  const onZoneDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
  };

  const onZoneDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = parsePaletteDrop(event);
    if (!payload) return;
    if (data.mcpSteps.length >= MCP_SOFT_LIMIT) {
      window.alert('Soft limit: at most 10 MCP steps per LLM.');
      return;
    }
    data.onDropMcpPalette(payload);
  };

  const onChipDragStart = (index: number) => (event: React.DragEvent) => {
    event.stopPropagation();
    setDragMcpIndex(index);
    event.dataTransfer.setData('application/x-llm-mcp-reorder', String(index));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onChipDragEnd = () => setDragMcpIndex(null);

  const onChipDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const onChipDropOnIndex = (toIndex: number) => (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const fromStr = event.dataTransfer.getData('application/x-llm-mcp-reorder');
    if (fromStr === '') return;
    const from = parseInt(fromStr, 10);
    if (Number.isNaN(from)) return;
    if (from === toIndex) return;
    data.onReorderMcp(from, toIndex);
  };

  return (
    <div className={`llm-node${selected ? ' llm-node--selected' : ''}`}>
      <Handle type="target" position={Position.Left} id="in" className="llm-node__handle llm-node__handle--target" />

      <div className="llm-node__header">
        <span className="llm-node__dot" aria-hidden />
        <span className="llm-node__title">{data.label}</span>
        <select
          className="llm-node__op nodrag"
          value={data.operation}
          onChange={onHeaderOpChange}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Operation"
        >
          {OPS.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </select>
      </div>

      <div className="llm-node__body nodrag">
        <div className="llm-node__prompt-wrap">
          <span className="llm-node__prompt-label">Prompt:</span>
          <div className="llm-node__prompt-preview">{preview || '—'}</div>
        </div>

        <div className="llm-node__mcp-label">MCP Sequence</div>
        <div className="llm-node__mcp-list">
          {data.mcpSteps.map((step, index) => (
            <div
              key={step.id}
              className={`llm-node__chip nodrag${step.kind === 'event' ? ' llm-node__chip--event' : ''}${
                dragMcpIndex === index ? ' llm-node__chip--dragging' : ''
              }`}
              draggable
              onDragStart={onChipDragStart(index)}
              onDragEnd={onChipDragEnd}
              onDragOver={onChipDragOver}
              onDrop={onChipDropOnIndex(index)}
            >
              <span className="llm-node__chip-handle" title="Drag to reorder">
                ≡
              </span>
              <span className="llm-node__chip-num">{index + 1}</span>
              <span className="llm-node__chip-icon" aria-hidden>
                {chipIcon(step.kind)}
              </span>
              <span className="llm-node__chip-name">{step.label}</span>
              <span className="llm-node__chip-op">{chipSuffix(step)}</span>
              <button
                type="button"
                className="llm-node__chip-remove"
                aria-label={`Remove ${step.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  data.onRemoveMcp(step.id);
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <div
          className={`llm-node__dropzone${data.mcpSteps.length === 0 ? ' llm-node__dropzone--empty' : ''}`}
          onDragOver={onZoneDragOver}
          onDrop={onZoneDrop}
        >
          + Drop MCP here
        </div>
      </div>

      <Handle type="source" position={Position.Right} id="out" className="llm-node__handle llm-node__handle--source" />
    </div>
  );
}
